import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

// Warm palette (row 1, top) — reds / oranges / yellows, 6 clearly distinct tints
const warmPalette = [
  { bg: 'bg-red-200', text: 'text-red-950', border: 'border-red-400', accent: 'border-l-red-600', hoverBorder: 'hover:border-red-500', hover: 'hover:shadow-red-200', ring: 'ring-red-500', addBtn: 'bg-red-500', watermark: 'bg-red-600', info: 'text-red-900', tooltip: 'bg-red-100/95 border-red-300' },
  { bg: 'bg-amber-300', text: 'text-amber-950', border: 'border-amber-500', accent: 'border-l-amber-700', hoverBorder: 'hover:border-amber-600', hover: 'hover:shadow-amber-300', ring: 'ring-amber-600', addBtn: 'bg-amber-600', watermark: 'bg-amber-700', info: 'text-amber-950', tooltip: 'bg-amber-200/95 border-amber-400' },
  { bg: 'bg-orange-200', text: 'text-orange-950', border: 'border-orange-400', accent: 'border-l-orange-600', hoverBorder: 'hover:border-orange-500', hover: 'hover:shadow-orange-200', ring: 'ring-orange-500', addBtn: 'bg-orange-500', watermark: 'bg-orange-600', info: 'text-orange-900', tooltip: 'bg-orange-100/95 border-orange-300' },
  { bg: 'bg-rose-300', text: 'text-rose-950', border: 'border-rose-500', accent: 'border-l-rose-700', hoverBorder: 'hover:border-rose-600', hover: 'hover:shadow-rose-300', ring: 'ring-rose-600', addBtn: 'bg-rose-600', watermark: 'bg-rose-700', info: 'text-rose-950', tooltip: 'bg-rose-200/95 border-rose-400' },
  { bg: 'bg-yellow-200', text: 'text-yellow-950', border: 'border-yellow-400', accent: 'border-l-yellow-600', hoverBorder: 'hover:border-yellow-500', hover: 'hover:shadow-yellow-200', ring: 'ring-yellow-500', addBtn: 'bg-yellow-500', watermark: 'bg-yellow-600', info: 'text-yellow-900', tooltip: 'bg-yellow-100/95 border-yellow-300' },
  { bg: 'bg-red-400', text: 'text-red-950', border: 'border-red-600', accent: 'border-l-red-800', hoverBorder: 'hover:border-red-700', hover: 'hover:shadow-red-400', ring: 'ring-red-700', addBtn: 'bg-red-600', watermark: 'bg-red-800', info: 'text-red-950', tooltip: 'bg-red-300/95 border-red-500' },
];

// Cold palette (row 2, bottom) — blues / greens / violets, 6 clearly distinct tints
const coldPalette = [
  { bg: 'bg-sky-200', text: 'text-sky-950', border: 'border-sky-400', accent: 'border-l-sky-600', hoverBorder: 'hover:border-sky-500', hover: 'hover:shadow-sky-200', ring: 'ring-sky-500', addBtn: 'bg-sky-500', watermark: 'bg-sky-600', info: 'text-sky-900', tooltip: 'bg-sky-100/95 border-sky-300' },
  { bg: 'bg-violet-200', text: 'text-violet-950', border: 'border-violet-400', accent: 'border-l-violet-600', hoverBorder: 'hover:border-violet-500', hover: 'hover:shadow-violet-200', ring: 'ring-violet-500', addBtn: 'bg-violet-500', watermark: 'bg-violet-600', info: 'text-violet-900', tooltip: 'bg-violet-100/95 border-violet-300' },
  { bg: 'bg-emerald-200', text: 'text-emerald-950', border: 'border-emerald-400', accent: 'border-l-emerald-600', hoverBorder: 'hover:border-emerald-500', hover: 'hover:shadow-emerald-200', ring: 'ring-emerald-500', addBtn: 'bg-emerald-500', watermark: 'bg-emerald-600', info: 'text-emerald-900', tooltip: 'bg-emerald-100/95 border-emerald-300' },
  { bg: 'bg-blue-400', text: 'text-blue-950', border: 'border-blue-600', accent: 'border-l-blue-800', hoverBorder: 'hover:border-blue-700', hover: 'hover:shadow-blue-400', ring: 'ring-blue-700', addBtn: 'bg-blue-600', watermark: 'bg-blue-800', info: 'text-blue-950', tooltip: 'bg-blue-300/95 border-blue-500' },
  { bg: 'bg-purple-300', text: 'text-purple-950', border: 'border-purple-500', accent: 'border-l-purple-700', hoverBorder: 'hover:border-purple-600', hover: 'hover:shadow-purple-300', ring: 'ring-purple-600', addBtn: 'bg-purple-600', watermark: 'bg-purple-700', info: 'text-purple-950', tooltip: 'bg-purple-200/95 border-purple-400' },
  { bg: 'bg-green-400', text: 'text-green-950', border: 'border-green-600', accent: 'border-l-green-800', hoverBorder: 'hover:border-green-700', hover: 'hover:shadow-green-400', ring: 'ring-green-700', addBtn: 'bg-green-600', watermark: 'bg-green-800', info: 'text-green-950', tooltip: 'bg-green-300/95 border-green-500' },
];

function getColorTheme(tone: 'cold' | 'warm', colorIndex: number) {
  const palette = tone === 'cold' ? coldPalette : warmPalette;
  return palette[colorIndex % 6];
}

interface CriteriaCardProps {
  id: number;
  code: string;
  name: string;
  description: string;
  points: number;
  index: number;
  tone: 'cold' | 'warm';
  colorIndex: number;
  onAdd: () => void;
  onRemove: () => void;
  isMaxReached: boolean; // When remainingPoints === 0
  canAdd: boolean; // When totalPoints < maxSelections
}

export const CriteriaCard: React.FC<CriteriaCardProps> = ({
  id,
  code,
  name,
  description,
  points,
  index,
  tone,
  colorIndex,
  onAdd,
  onRemove,
  isMaxReached,
  canAdd,
}) => {
  const { i18n } = useTranslation();
  const theme = getColorTheme(tone, colorIndex);

  // SVG icon path - matching app_modern.js logic exactly
  // j starts at 2, increments: 2,3,4,5,6,7,8,9,10,11, then when j > 10, j = 1
  let svgNumber;
  if (index < 10) {
    svgNumber = index + 2; // 2 to 11 (first 10 items)
  } else {
    const cycleIndex = (index - 10) % 11;
    svgNumber = cycleIndex === 0 ? 1 : cycleIndex + 1; // 1, then 2-11, then 1, then 2-11...
  }
  const svgPath = `/img/svg${svgNumber}.svg`;

  // Animation states
  const [pointsAnimating, setPointsAnimating] = useState(false);
  const [watermarkScale, setWatermarkScale] = useState<'scale-100' | 'scale-110' | 'scale-90'>('scale-100');
  const [ringActive, setRingActive] = useState(false);
  const [prevPoints, setPrevPoints] = useState(points);
  const [tooltipHovered, setTooltipHovered] = useState(false);
  const [tooltipPinned, setTooltipPinned] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTooltipTimeoutRef = useRef<number>();

  const showTooltip = tooltipHovered || tooltipPinned;

  const updateTooltipPosition = useCallback(() => {
    if (!infoButtonRef.current) return;

    const rect = infoButtonRef.current.getBoundingClientRect();
    const tooltipWidth = 256;
    const gap = 8;
    let left = rect.left;
    const top = rect.bottom + gap;

    if (left + tooltipWidth > window.innerWidth - 16) {
      left = window.innerWidth - tooltipWidth - 16;
    }
    if (left < 16) left = 16;

    setTooltipPosition({ top, left });
  }, []);

  const openTooltipHover = () => {
    window.clearTimeout(hideTooltipTimeoutRef.current);
    setTooltipHovered(true);
  };

  const closeTooltipHover = () => {
    hideTooltipTimeoutRef.current = window.setTimeout(() => {
      setTooltipHovered(false);
    }, 120);
  };

  // Get point label (singular/plural) matching app_modern.js
  const getPointLabel = (count: number): string => {
    if (i18n.language === 'fr') {
      return count <= 1 ? 'Point' : 'Points';
    } else {
      return count === 1 ? 'Point' : 'Points';
    }
  };

  // Trigger animation when points change
  useEffect(() => {
    if (points !== prevPoints) {
      // Bounce animation for points
      setPointsAnimating(true);
      const timer = setTimeout(() => setPointsAnimating(false), 500);

      // Watermark scale animation
      if (points > prevPoints) {
        // Adding point: scale up
        setWatermarkScale('scale-110');
        setTimeout(() => setWatermarkScale('scale-100'), 150);
        // Ring effect
        setRingActive(true);
        setTimeout(() => setRingActive(false), 300);
      } else if (points < prevPoints) {
        // Removing point: scale down
        setWatermarkScale('scale-90');
        setTimeout(() => setWatermarkScale('scale-100'), 150);
      }

      setPrevPoints(points);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [points, prevPoints]);

  useEffect(() => {
    if (!tooltipPinned) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        infoButtonRef.current?.contains(target) ||
        tooltipRef.current?.contains(target)
      ) {
        return;
      }
      setTooltipPinned(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tooltipPinned]);

  useEffect(() => {
    if (!showTooltip) {
      setTooltipPosition(null);
      return;
    }

    updateTooltipPosition();
    window.addEventListener('scroll', updateTooltipPosition, true);
    window.addEventListener('resize', updateTooltipPosition);

    return () => {
      window.removeEventListener('scroll', updateTooltipPosition, true);
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [showTooltip, updateTooltipPosition]);

  useEffect(() => {
    return () => window.clearTimeout(hideTooltipTimeoutRef.current);
  }, []);

  // Determine if card should be grayscale (matching app_modern.js logic)
  const isDisabled = isMaxReached && points === 0;

  return (
    <div
      className="criteria_container h-full animate__animated animate__fadeInUp"
      style={{
        animationDuration: '0.5s',
        animationDelay: `${index * 0.1}s`,
        animationFillMode: 'both'
      }}
    >
      <div
        className={`criteria_card h-full ${theme.bg} rounded-2xl border border-l-[6px] ${theme.border} ${theme.accent} shadow-sm hover:shadow-md ${theme.hoverBorder} ${theme.hover} transition-all duration-300 ease-out flex flex-col overflow-hidden relative group cursor-pointer ${
          ringActive ? `ring-2 ${theme.ring} ring-offset-2` : ''
        } ${
          isDisabled ? 'opacity-40 grayscale' : ''
        }`}
        data-id={id}
        data-code={code}
        data-points={points}
        data-theme-ring={theme.ring}
        data-theme-text={theme.text}
      >
        {/* Watermark: Icon by default (0), number when points > 0 */}
        <div className={`watermark-container absolute -bottom-10 -right-10 pointer-events-none select-none transition-all duration-500 transform -rotate-12 z-0 group-hover:-translate-x-6 group-hover:-translate-y-6 group-hover:scale-105 ${watermarkScale} ${
          points > 0 ? 'opacity-[0.1]' : 'opacity-20'
        }`}>
          {points > 0 ? (
            <div className={`text-[12rem] font-black ${theme.text} leading-none`}>{points}</div>
          ) : (
            <div
              className={`w-48 h-48 ${theme.watermark} opacity-40`}
              style={{
                WebkitMaskImage: `url(${svgPath})`,
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskImage: `url(${svgPath})`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
              }}
              aria-hidden="true"
            />
          )}
        </div>

        <div className="p-5 flex flex-col flex-grow relative z-10">
          <span className={`absolute top-3 right-3 text-[10px] font-mono tracking-wider ${theme.text} opacity-30 select-none pointer-events-none`}>
            {code}
          </span>

          {/* Header */}
          <div className="mb-3">
            <button
              ref={infoButtonRef}
              type="button"
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border ${theme.border} bg-white/70 ${theme.info} shadow-sm transition-all hover:bg-white hover:shadow ${
                showTooltip ? `ring-2 ring-offset-1 ${theme.ring}` : ''
              }`}
              aria-label="Informations"
              aria-expanded={showTooltip}
              onMouseEnter={openTooltipHover}
              onMouseLeave={closeTooltipHover}
              onClick={(e) => {
                e.stopPropagation();
                setTooltipPinned((prev) => !prev);
              }}
            >
              i
            </button>
          </div>

          {/* Title (description lives in the tooltip only) */}
          <div className="mb-2 flex-grow">
            <h3 className={`text-lg sm:text-xl font-bold ${theme.text} mb-1 leading-tight`}>
              {name}
            </h3>
          </div>

          {/* Compact Actions Footer */}
          <div className={`mt-auto pt-3 border-t ${theme.border} flex items-center justify-between`}>
            {/* Minus Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              disabled={points === 0 || isDisabled}
              className="criteria_minus w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-300 hover:shadow flex items-center justify-center transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Retirer 1 point"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>

            {/* Points Display */}
            <div className="flex flex-col items-center">
              <div className={`criteria_points text-2xl font-black ${theme.text} tabular-nums animate__animated ${
                pointsAnimating ? 'animate__bounceIn' : ''
              }`}>
                {points}
              </div>
              <span className="criteria_points_label text-[10px] text-slate-500 uppercase font-semibold tracking-wider">
                {getPointLabel(points)}
              </span>
            </div>

            {/* Add Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAdd();
              }}
              disabled={!canAdd || isDisabled}
              className={`criteria_add w-10 h-10 rounded-full ${theme.addBtn} text-white border-2 ${theme.border} hover:brightness-110 flex items-center justify-center transition-all active:scale-95 shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed`}
              aria-label="Ajouter 1 point"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {showTooltip && tooltipPosition && createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          className={`fixed z-[9999] w-64 rounded-xl border backdrop-blur-sm p-3 shadow-xl ${theme.tooltip}`}
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
          onMouseEnter={openTooltipHover}
          onMouseLeave={closeTooltipHover}
        >
          <p className={`text-sm ${theme.text} leading-relaxed`}>{description}</p>
        </div>,
        document.body
      )}
    </div>
  );
};
